import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Field, Input, useFocusTrap } from '@truecare/ui';
import { messageOf, unwrap } from '@truecare/api-client';
import { api } from '../api';

/**
 * Shared first-time TOTP enrollment modal — used by any persona's account
 * Security surface (caregiver, facility user, homecare client). 2FA is opt-in
 * in the portal; operators are required to have it and enroll in the admin app.
 *
 * Flow: intro → POST /auth/totp/enroll (returns secret + otpauth + QR data URL)
 * → scan → POST /auth/totp/verify { code } → done. All endpoints are User-level,
 * so nothing here is persona-specific.
 */

interface EnrollResult {
  secret: string;
  otpauthUrl: string;
  qrCodeDataUrl: string;
}

type Step = 'intro' | 'scan' | 'done';

export function TotpEnrollModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>('intro');
  const [enroll, setEnroll] = useState<EnrollResult | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Focus in on open, trap Tab, Escape-to-close, restore focus to the
  // trigger on close (H-F4). Replaces the prior Escape-only handler.
  useFocusTrap(panelRef, { onEscape: onClose });

  async function begin(): Promise<void> {
    setPending(true);
    setError(null);
    try {
      const r = (await unwrap(
        api.POST('/api/v1/auth/totp/enroll' as never, {} as never),
      )) as unknown as EnrollResult;
      setEnroll(r);
      setStep('scan');
    } catch (e) {
      setError(messageOf(e, 'Could not start 2FA setup.'));
    } finally {
      setPending(false);
    }
  }

  async function verify(): Promise<void> {
    setPending(true);
    setError(null);
    try {
      await unwrap(
        api.POST('/api/v1/auth/totp/verify' as never, {
          body: { code },
        } as never),
      );
      await qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      setStep('done');
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : 'That code did not verify. Enter the current 6-digit code from your app.',
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="totp-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      data-testid="account-totp-modal"
    >
      <div className="w-full max-w-md rounded-md border border-border bg-cream p-6 max-h-[90vh] overflow-y-auto">
        <h2 id="totp-modal-title" className="font-heading text-xl text-forest">
          Two-factor authentication
        </h2>

        {error && (
          <Alert tone="danger" className="mt-3">
            {error}
          </Alert>
        )}

        {step === 'intro' && (
          <>
            <p className="mt-3 font-body text-sm text-ink-muted">
              Add a second layer of security to your account. You'll scan a QR
              code with an authenticator app (Google Authenticator, 1Password,
              Authy, etc.) and enter the 6-digit code it shows.
            </p>
            <div className="mt-5 flex items-center justify-end gap-3">
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                loading={pending}
                onClick={() => void begin()}
                data-testid="account-totp-begin"
              >
                Start setup
              </Button>
            </div>
          </>
        )}

        {step === 'scan' && enroll && (
          <>
            <p className="mt-3 font-body text-sm text-ink-muted">
              Scan this QR code in your authenticator app, then enter the 6-digit
              code it shows to confirm.
            </p>
            <img
              src={enroll.qrCodeDataUrl}
              alt="Two-factor authentication QR code"
              className="mx-auto mt-4 h-44 w-44"
            />
            <p className="mt-2 text-center text-xs text-ink-muted">
              Can't scan? Enter this key manually:{' '}
              <span className="font-mono break-all">{enroll.secret}</span>
            </p>
            <Field label="6-digit code" className="mt-4">
              <Input
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                data-testid="account-totp-code"
              />
            </Field>
            <div className="mt-5 flex items-center justify-end gap-3">
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={code.length !== 6}
                loading={pending}
                onClick={() => void verify()}
                data-testid="account-totp-verify"
              >
                Verify + enable
              </Button>
            </div>
          </>
        )}

        {step === 'done' && (
          <>
            <Alert
              tone="success"
              className="mt-3"
              title="Two-factor authentication enabled"
            >
              Your account is now protected with 2FA. You'll be asked for a code
              from your authenticator app when you sign in.
            </Alert>
            <div className="mt-5 flex justify-end">
              <Button type="button" variant="primary" onClick={onClose}>
                Done
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
